import { DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  Renderer2,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  NgbDateStruct,
  NgbDropdown,
  NgbDropdownModule,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import {
  TranslateDirective,
  TranslatePipe,
  TranslateService,
} from '@ngx-translate/core';
import { LocalizedDatePipe } from '@shared/pipes/localized-date.pipe';
import dayjs from 'dayjs/esm';
import { distinctUntilChanged, take } from 'rxjs';

import { ROUTE_NAMES } from '@constants';
import {
  AnyResource,
  AuthenticationMethod,
  CommunityProfileType,
  CommunityStatus,
  CommunitySubType,
  PermissionLevel,
  ResourceType,
  UserSubType,
} from '@models';
import {
  AccountService,
  AuthenticationService,
  CommunityService,
  DateFormatService,
  i18nService,
  RoutesService,
  UserBeanService,
} from '@services';
import { ClickableDirective } from '@shared/directives/clickable.directive';
import { DropdownSheetDirective } from '@shared/directives/dropdown-sheet';
import { LocaleKeyPipe } from '@shared/pipes/locale-key.pipe';
import { DateInputComponent } from '@shared/ui/date-input/date-input.component';
import { ProfileImageComponent } from '@shared/ui/profile-image/profile-image.component';
import { isSet } from '@shared/utils';
import {
  AppState,
  injectLocale,
  injectUserBean,
  resetIsSearchScopedByResource,
  resetSearchParams,
  SearchParams,
  setIsLoggedIn,
  setIsSearchScopedByResource,
  setNavigationOpen,
  setOriginPath,
  setSearchQuery,
  updateSearchParams,
  updateUserData,
} from '@store';
import { AlertsWidgetComponent } from './alerts-widget.component';
import { FavoritesWidgetComponent } from './favorites-widget.component';

@Component({
  selector: 'app-header',
  imports: [
    DateInputComponent,
    TranslatePipe,
    TranslateDirective,
    AlertsWidgetComponent,
    FavoritesWidgetComponent,
    ProfileImageComponent,
    FormsModule,
    RouterLink,
    NgbTooltipModule,
    NgbDropdownModule,
    LocaleKeyPipe,
    DropdownSheetDirective,
    ClickableDirective,
    DatePipe,
    LocalizedDatePipe,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  translate = inject(TranslateService);
  store = inject<Store<AppState>>(Store);
  router = inject(Router);
  cdr = inject(ChangeDetectorRef);
  authenticationService = inject(AuthenticationService);
  userBeanService = inject(UserBeanService);
  i18n = inject(i18nService);
  accountService = inject(AccountService);
  routesService = inject(RoutesService);
  dateFormatService = inject(DateFormatService);
  communityService = inject(CommunityService);
  renderer = inject(Renderer2);

  headerUserLabel = viewChild('headerUserLabel', { read: ElementRef });
  languagesDropdown = viewChild<NgbDropdown>('languagesDropdown');

  userBean = injectUserBean();
  personalRoomId = computed(() => {
    const { roomId, roomPermission, communityStatus, communityProfileId } =
      this.userBean();

    return isSet(roomId) &&
      roomPermission > 0 &&
      communityStatus !== CommunityStatus.SUSPENDED &&
      communityProfileId !== CommunityProfileType.SIGNING_ROOM
      ? roomId
      : null;
  });
  navigation = toSignal(
    this.store.select((state) => state.navigation),
    { requireSync: true }
  );
  query = toSignal(
    this.store.select((state) => state.search.query),
    { requireSync: true }
  );
  locale = injectLocale();
  languages = toSignal(
    this.store.select((state) => state.languages),
    { requireSync: true }
  );
  currentResourceState = toSignal(
    this.store.select((state) => state.resource.current),
    { requireSync: true }
  );
  params = toSignal(
    this.store.select((state) => state.search.params),
    { requireSync: true }
  );
  isSearchScopedByCurrentResource = toSignal(
    this.store.select((state) => state.search.isSearchScopedByCurrentResource),
    { requireSync: true }
  );

  validDate = signal<{
    from: boolean;
    to: boolean;
    errorIntervalDate?: boolean;
  }>({
    from: true,
    to: true,
  });
  maxDate = signal<NgbDateStruct | undefined>(undefined);
  dateFormatter: string = '';
  selectLang = false;
  fromDateModel: Nullable<string> = null;
  toDateModel: Nullable<string> = null;
  resourceScopeOptionContext = signal<Nullable<Partial<AnyResource>>>(null);
  isDestroyed = false;
  currentRoutePath = '';

  UserSubType = UserSubType;
  CommunityProfileType = CommunityProfileType;
  ResourceType = ResourceType;
  ROUTE_NAMES = ROUTE_NAMES;
  CommunitySubType = CommunitySubType;
  AuthenticationMethod = AuthenticationMethod;
  PermissionLevel = PermissionLevel;

  constructor() {
    effect(() => {
      const params = this.params();

      untracked(() => {
        this.fromDateModel = params.from;
        this.toDateModel = params.to;
        this.scheduleResourceScopeSync();
      });
    });

    effect(() => {
      this.currentResourceState();

      untracked(() => {
        this.scheduleResourceScopeSync();
      });
    });

    this.routesService.lastNavigationPath
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((path) => {
        this.currentRoutePath = path;
        this.scheduleResourceScopeSync();
      });
  }

  ngOnInit() {
    this.dateFormatter = this.dateFormatService.dateFormatter();

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.initDate();
        this.cdr.detectChanges();
      });

    if (
      this.userBean().communityProfileId !== CommunityProfileType.SIGNING_ROOM
    ) {
      this.resetSearch();
      this.initDate();
    }

    this.setCommunityLogo();
    this.scheduleResourceScopeSync();

    this.destroyRef.onDestroy(() => {
      this.isDestroyed = true;
    });
  }

  scheduleResourceScopeSync = () => {
    if (this.isDestroyed) {
      return;
    }

    Promise.resolve().then(() => {
      if (this.isDestroyed) {
        return;
      }

      this.updateResourceScopeOption();
    });
  };

  updateResourceScopeOption = () => {
    const shouldRender =
      (this.currentRoutePath.includes('rooms/') ||
        this.currentRoutePath.includes('events/')) &&
      !!this.currentResourceState();

    if (!shouldRender) {
      this.resourceScopeOptionContext.set(null);

      return;
    }

    if (this.resourceScopeOptionContext() === this.currentResourceState()) {
      return;
    }

    this.resourceScopeOptionContext.set(this.currentResourceState());
  };

  resetSearch = () => {
    this.store.dispatch(resetSearchParams());
    this.store.dispatch(resetIsSearchScopedByResource());
    this.updateQuery('');
  };

  setCommunityLogo = () => {
    if (this.userBean()?.communityLogo) {
      const now = new Date().getTime();

      this.communityService
        .getCommunityImage(now)
        .pipe(take(1))
        .subscribe({
          next: (data) => {
            this.store.dispatch(
              updateUserData({ payload: { communityLogoUrl: data } })
            );
          },
          error: () => {
            this.store.dispatch(
              updateUserData({
                payload: { communityLogo: false, communityLogoUrl: undefined },
              })
            );
          },
        });
    } else {
      this.store.dispatch(
        updateUserData({ payload: { communityLogo: false } })
      );
    }

    setTimeout(() => {
      if (
        !this.navigation().route ||
        this.navigation().route === ROUTE_NAMES.SEARCH
      ) {
        this.resetSearch();
      } else {
        const { to, from } = this.params();

        if (!from || !to) {
          this.initDate();
        }
      }
    }, 500);
  };

  setLocale = (locale: string) => {
    this.i18n.setLocale(locale);
  };

  logout = () =>
    this.authenticationService.logout().then(async () => {
      this.routesService.resetPreviousRoute();
      this.routesService.clearSessionRestoreUrl();
      this.userBeanService.resetUserBean();
      this.store.dispatch(
        setIsLoggedIn({ payload: this.authenticationService.isLoggedIn() })
      );

      await this.router.navigate(['/login'], {
        queryParams: {},
        queryParamsHandling: '',
      });

      window.history.replaceState({}, '', '/login');
    });

  changePassword = (rid: number) => {
    this.accountService.changePassword(rid, this.userBean().rootUsersId);
  };

  userSupport = () => {
    this.accountService.userSupport();

    if (this.headerUserLabel()?.nativeElement) {
      this.renderer.removeClass(
        this.headerUserLabel()!.nativeElement,
        'active'
      );
    }
  };

  backHome = () => {
    if (this.navigation().open) {
      this.toggleNavigation();
    }

    if (this.userBean() && this.userBean().communityStatus === 3) {
      this.router.navigate(['/billing']);
    } else if (
      this.userBean().communityProfileId ===
        CommunityProfileType.PROJECT_ROOM ||
      this.userBean().communityProfileId === undefined
    ) {
      this.router.navigate(['/rooms']);
    } else {
      this.router.navigate(['/home']);
    }
  };

  initDate = () => {
    const { format } = this.dateFormatService;
    const now = new Date();

    this.validDate.set({ from: true, to: true });

    this.maxDate.set({
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    });

    const nowDayjs = dayjs();
    const to = nowDayjs.startOf('day');
    const from = to.subtract(1, 'month');

    this.updateFilter({
      from: dayjs(from).format(format()),
      to: dayjs(to).format(format()),
    });
  };

  toggleNavigation = (shouldClose?: boolean) => {
    this.store.dispatch(
      setNavigationOpen({
        payload: shouldClose !== undefined ? false : !this.navigation().open,
      })
    );
  };

  openDropdownLanguages = () => {
    setTimeout(() => {
      this.languagesDropdown()?.open();
    }, 100);
  };

  initialSearch = (isPeriodUndefined?: boolean) => {
    if (isPeriodUndefined) {
      this.updateFilter({ isCustomDate: true });

      return;
    }

    const originPath = this.getSearchOriginPath();

    if (this.navigation().open) {
      this.toggleNavigation();
    }

    if (
      this.currentResourceState()?.type &&
      [ResourceType.ROOM, ResourceType.EVENT].includes(
        this.currentResourceState()!.type!
      ) &&
      this.isSearchScopedByCurrentResource()
    ) {
      this.updateFilter({
        rid: this.currentResourceState()!.rid,
      });
    }

    if (!this.params().isCustomDate) {
      this.updateFilter({
        from: null,
        to: null,
      });
    }

    const { period } = this.params();

    if (this.query() || period === 1 || period === 7) {
      this.router.navigate(['/search']);
    }

    if (originPath !== '/search') {
      this.store.dispatch(setOriginPath({ payload: originPath }));
    }
  };

  getSearchOriginPath = () => {
    const { pathname, search } = window.location;
    const params = new URLSearchParams(search);

    ['file', 'discussion', 'vote', 'task', 'signprocess'].forEach((name) =>
      params.delete(name)
    );

    const query = params.toString();

    return query ? `${pathname}?${query}` : pathname;
  };

  hasCommunityExpiryMessage = () =>
    this.userBean().communityExpirationDaysCount !== undefined &&
    ((this.userBean().communitySubtype === CommunitySubType.TRIAL &&
      this.userBean().communityExpirationDaysCount === 0) ||
      (this.userBean().communitySubtype === CommunitySubType.TRIAL &&
        this.userBean().communityExpirationDaysCount === 1) ||
      (this.userBean().communitySubtype === CommunitySubType.TRIAL &&
        this.userBean().communityExpirationDaysCount === 2) ||
      (this.userBean().communitySubtype === CommunitySubType.TRIAL &&
        this.userBean().communityExpirationDaysCount > 2));

  isFromGtTo = () => {
    if (this.fromDateModel && this.toDateModel) {
      const from = this.dateFormatService.modelToDate(this.fromDateModel);
      const to = this.dateFormatService.modelToDate(this.toDateModel);

      if (!from || !to) {
        return false;
      }

      return +from > +to;
    }

    return false;
  };

  updateQuery = (value: string) => {
    this.store.dispatch(setSearchQuery({ payload: value }));
  };

  getInputValue = (event: Event) => (event.target as HTMLInputElement).value;

  updateFilter = (filter: Partial<SearchParams>) => {
    this.store.dispatch(updateSearchParams({ payload: { ...filter } }));
  };

  getSelectValue = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    const [, stringValue] = target.value.split(': ');

    if (stringValue === undefined || stringValue === 'undefined') {
      return undefined;
    } else if (stringValue === 'null') {
      return null;
    }

    return +stringValue;
  };

  setIsSearchScopedByResource = (payload: boolean) => {
    this.store.dispatch(setIsSearchScopedByResource({ payload }));
  };
}
